/**
 * <shipping-rate>
 *
 * Zeigt die Live-Versandkosten im Cart-Drawer.
 * Quelle: /cart/shipping_rates.json (reflektiert Shopify-Admin-Aenderungen sofort)
 *
 * Frontend-Override: Wenn der Warenkorb laut Theme-Settings die
 * Bedingungen fuer Gratisversand erfuellt (Gewicht < max, Preis >= Schwelle),
 * wird "Gratis" angezeigt – auch wenn die Shopify-Versandregel selbst noch
 * nicht angepasst wurde.
 */
class ShippingRateElement extends HTMLElement {
  constructor() {
    super();
    this._fetched = false;
  }

  connectedCallback() {
    if (this._fetched) return;
    this._fetched = true;

    const priceCents = parseInt(this.dataset.totalPriceCents || '0', 10);

    // Leerer Warenkorb: nichts anzeigen
    if (priceCents === 0) {
      this.style.display = 'none';
      return;
    }

    // Frontend-Override: Gratisversand wenn Schwelle erreicht und Gewicht unter Max
    const thresholdCents = parseInt(this.dataset.freeThresholdCents || '0', 10);
    const maxWeightG = parseInt(this.dataset.maxFreeWeightG || '0', 10);
    const totalWeightG = parseInt(this.dataset.totalWeightG || '0', 10);

    if (thresholdCents > 0 && priceCents >= thresholdCents && totalWeightG < maxWeightG) {
      this._render('free');
      return;
    }

    // Versandkosten live von Shopify abfragen.
    this._fetchLiveRate();
  }

  async _fetchLiveRate() {
    const url =
      '/cart/shipping_rates.json?shipping_address%5Bcountry%5D=Switzerland&shipping_address%5Bzip%5D=8000';

    try {
      let data = null;
      // /cart/shipping_rates.json kann initial 422 liefern (Berechnung laeuft).
      // Bis zu 10x mit 300ms Pause pollen.
      for (let i = 0; i < 10; i++) {
        const res = await fetch(url);
        if (res.status === 200) {
          data = await res.json();
          if (data.shipping_rates && data.shipping_rates.length > 0) break;
          data = null;
        }
        await new Promise((r) => setTimeout(r, 300));
      }

      if (!data || !data.shipping_rates || data.shipping_rates.length === 0) {
        this._render('unknown');
        return;
      }

      // Guenstigste Rate
      const cheapest = data.shipping_rates.reduce((min, r) =>
        parseFloat(r.price) < parseFloat(min.price) ? r : min
      );
      const price = parseFloat(cheapest.price);

      if (price === 0) {
        this._render('free');
      } else {
        this._render('paid', price);
      }
    } catch (e) {
      this._render('unknown');
    }
  }

  _updateDeliveryDate() {
    const el = this.querySelector('.fsb-shipping__delivery');
    if (!el) return;

    // Spracherkennung: <html lang="de"> oder Fallback
    const lang = (document.documentElement.lang || 'de').substring(0, 2);
    const dayNames = lang === 'en'
      ? ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
      : ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

    function addBusinessDays(date, n) {
      const result = new Date(date);
      let added = 0;
      while (added < n) {
        result.setDate(result.getDate() + 1);
        if (result.getDay() !== 0 && result.getDay() !== 6) added++;
      }
      return result;
    }

    const today = new Date();
    const earliest = addBusinessDays(today, 1);
    const latest = addBusinessDays(today, 3);

    const labelEarliest = this.dataset.deliveryEarliest || 'Zwischen';
    const labelLatest = this.dataset.deliveryLatest || 'und';
    const labelSuffix = this.dataset.deliverySuffix || 'bei dir zu Hause.';

    el.textContent = `📦 ${labelEarliest} ${dayNames[earliest.getDay()]} ${labelLatest} ${dayNames[latest.getDay()]} ${labelSuffix}`;
  }

  _render(state, price) {
    const valueEl = this.querySelector('.fsb-shipping__value');
    const grandEl = this.querySelector('.fsb-grandtotal__value');
    if (!valueEl) return;

    this.classList.remove('fsb-shipping--free', 'fsb-shipping--paid', 'fsb-shipping--unknown');

    const currency = this.dataset.currency || 'CHF';
    const subtotalCents = parseInt(this.dataset.totalPriceCents || '0', 10);
    let shippingCents = 0;

    if (state === 'free') {
      valueEl.textContent = this.dataset.labelFree || 'Gratis';
      this.classList.add('fsb-shipping--free');
      shippingCents = 0;
    } else if (state === 'paid') {
      valueEl.textContent = `${currency} ${price.toFixed(2)}`;
      this.classList.add('fsb-shipping--paid');
      shippingCents = Math.round(price * 100);
    } else {
      valueEl.textContent = this.dataset.labelUnknown || 'im Checkout berechnet';
      this.classList.add('fsb-shipping--unknown');
      // Bei unbekanntem Versand: Grand Total = Zwischensumme (kein Aufschlag anzeigen)
      shippingCents = 0;
    }

    if (grandEl) {
      const total = (subtotalCents + shippingCents) / 100;
      grandEl.textContent = `${currency} ${total.toFixed(2)}`;
    }

    this._updateDeliveryDate();
  }
}

if (!customElements.get('shipping-rate')) {
  customElements.define('shipping-rate', ShippingRateElement);
}
